import type {CSSProperties} from 'react';
import {Check, CheckCheck, Circle, Eye} from 'lucide-react';
import {Badge} from '@ui/shadcn/badge';
import {cn} from '@lib/utils';
import {
  TASK_STATUS_DOT,
  TASK_STATUS_LABELS,
  TASK_PRIORITY_BADGE,
  TASK_PRIORITY_LABELS,
  type TaskStatus,
  type TaskPriority,
} from '../model/types';

/** Роль текущего пользователя в задаче; не влияет на серверные права. */
export const TaskObserverBadge = ({
  actorSid,
  observerUserSid,
  className,
}: {
  actorSid?: string;
  observerUserSid?: string | null;
  className?: string;
}) => {
  if (!actorSid || observerUserSid !== actorSid) return null;
  return (
    <Badge
      variant='outline'
      className={cn(
        'px-2.5 py-1 text-[var(--color-muted-foreground)]',
        className,
      )}
      title='Вы наблюдатель этой задачи'
    >
      <Eye aria-hidden='true' />
      Наблюдатель
    </Badge>
  );
};

/** Акцентный статус задачи с существующей цветовой шкалой и признаком выполнения. */
export const TaskStatusBadge = ({status}: {status: TaskStatus}) => {
  const Icon =
    status === 'done' ? Check : status === 'partial' ? CheckCheck : Circle;
  return (
    <Badge
      variant='outline'
      style={{'--task-state-color': TASK_STATUS_DOT[status]} as CSSProperties}
      className='gap-1.5 border-[var(--task-state-color)]/40 bg-[var(--task-state-color)]/10 px-2.5 py-1 font-semibold text-[var(--color-foreground)]'
    >
      <Icon className='text-[var(--task-state-color)]' />
      {TASK_STATUS_LABELS[status]}
    </Badge>
  );
};

/** Приоритет задачи: общий словарь подписей и цветов, без отдельной шкалы модалки. */
export const TaskPriorityBadge = ({priority}: {priority: TaskPriority}) => {
  const tone = TASK_PRIORITY_BADGE[priority];
  return (
    <Badge
      variant='outline'
      style={{backgroundColor: tone.bg, color: tone.color}}
      className='border-current/20 px-2.5 py-1 font-semibold'
    >
      {TASK_PRIORITY_LABELS[priority]}
    </Badge>
  );
};
